import React from "react";
import { motion } from "framer-motion";
import { Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, Sparkles, Snowflake, Flag, Clover, Mail, HelpCircle } from "lucide-react";
import { PlayerAvatar, PIECE_COLORS } from "./PlayerAvatar";

const DICE = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];
const KIND_ICON = { start: Flag, bonus: Sparkles, penalty: Snowflake, note: Mail, clue: HelpCircle };

// 7x7 ring: 0 bottom-right, counter-clockwise.
export function cellOf(i) {
  if (i <= 6) return [6, 6 - i];
  if (i <= 12) return [6 - (i - 6), 0];
  if (i <= 18) return [0, i - 12];
  return [i - 18, 6];
}

export function Dice({ value, rolling, size = 56 }) {
  const D = DICE[(value || 1) - 1];
  return (
    <motion.div animate={rolling ? { rotate: [0, 90, 180, 270, 360] } : { rotate: 0 }} transition={rolling ? { repeat: Infinity, duration: 0.5 } : {}} data-testid="board-dice">
      <D style={{ width: size, height: size }} className="text-[var(--gold)]" strokeWidth={1.4} />
    </motion.div>
  );
}

export default function BoardView({ g, big = false, rolling = false }) {
  const st = g.state;
  const players = g.players || [];
  const color = Object.fromEntries((st?.order || []).map((pid, i) => [pid, PIECE_COLORS[i % PIECE_COLORS.length]]));
  const byPos = {};
  (st?.order || []).forEach((pid) => { const pos = st.pos[pid] || 0; (byPos[pos] = byPos[pos] || []).push(pid); });
  const pmap = Object.fromEntries(players.map((p) => [p.player_id, p]));
  const current = st?.order?.[st.turn % Math.max(1, st.order.length)];
  const owned = new Set(Object.values(st?.cards || {}).flat());
  const piece = big ? 30 : 18;
  return (
    <div className={`relative w-full aspect-square mx-auto ${big ? "max-w-[min(100%,calc(100vh-230px))]" : "max-w-[min(100%,82vh)]"}`} data-testid="board-view">
      <div className="grid grid-cols-7 grid-rows-7 gap-[3px] w-full h-full">
        {g.spaces.map((s) => {
          const [r, c] = cellOf(s.i);
          const Icon = KIND_ICON[s.kind];
          const corner = s.kind !== "clue" && s.kind !== "note";
          return (
            <div key={s.i} style={{ gridRow: r + 1, gridColumn: c + 1 }} data-testid={`board-space-${s.i}`}
              className={`relative rounded-md flex flex-col items-center justify-center text-center overflow-hidden ${corner ? "bg-[var(--gold)] text-black" : s.kind === "clue" ? "bg-white/10" : "bg-white/5"} ${st?.pending?.space === s.i ? "ring-2 ring-white" : ""}`}>
              <Icon className={`${big ? "w-5 h-5" : "w-3 h-3"} ${corner ? "" : "text-[var(--gold)]"} ${s.kind === "clue" && owned.has(s.clue) ? "opacity-30" : ""}`} />
              <span className={`${big ? "text-[11px]" : "text-[7px] sm:text-[8px]"} leading-tight px-0.5 mt-0.5`}>{s.label}</span>
              <div className="absolute inset-x-0 bottom-0.5 flex flex-wrap justify-center gap-0.5">
                {(byPos[s.i] || []).slice(0, 6).map((pid) => (
                  <motion.span layoutId={`piece-${pid}`} key={pid} transition={{ type: "spring", stiffness: 200, damping: 20 }}>
                    <PlayerAvatar p={pmap[pid]} size={piece} color={color[pid]} ring={pid === current && st.phase !== "over"} />
                  </motion.span>
                ))}
              </div>
            </div>
          );
        })}
        <div className="row-start-2 row-end-7 col-start-2 col-end-7 rounded-2xl bg-[radial-gradient(circle_at_center,#1f1a12,#0b0b0b)] border border-[var(--gold)]/30 flex flex-col items-center justify-center gap-3 p-3" data-testid="board-center">
          <img src="/brand/logo-mark.png" alt="Locked In Forever" className={big ? "h-28" : "h-14"} draggable="false" />
          <img src="/brand/logo-wordmark.png" alt="" className={big ? "h-7" : "h-3"} draggable="false" />
          {st?.last_roll && <Dice value={st.last_roll.value} rolling={rolling} size={big ? 64 : 36} />}
          {big && st?.log?.[0] && <p className="text-white/80 text-lg text-center font-serif" data-testid="board-last-log">{st.log[0]}</p>}
        </div>
      </div>
    </div>
  );
}

export function TurnBanner({ g, me }) {
  const st = g.state;
  if (!st) return null;
  const pmap = Object.fromEntries((g.players || []).map((p) => [p.player_id, p]));
  const pid = st.order[st.turn % Math.max(1, st.order.length)];
  if (st.phase === "over") return <p className="text-center font-serif text-2xl text-[var(--gold)]" data-testid="board-winner">{pmap[st.winner]?.name || "Someone"} collected every clue card!</p>;
  const mine = me && pid === me.player_id;
  return (
    <p className="text-center text-sm text-white/70" data-testid="board-turn">
      {mine ? <b className="text-[var(--gold)]">Your turn!</b> : <>Waiting on <b className="text-white">{pmap[pid]?.name || "…"}</b></>}
      {!pmap[pid]?.is_bot && g.seconds_left != null && <span className="text-white/40"> · {g.seconds_left}s</span>}
    </p>
  );
}

export function ClueCard({ g, onPick, result, disabled }) {
  const st = g.state;
  if (st?.phase !== "answer" || !st.pending) return null;
  const c = g.config.clues[st.pending.clue];
  const who = { bride: g.config.bride_name, groom: g.config.groom_name, couple: `${g.config.bride_name} & ${g.config.groom_name}` }[c.about];
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#FAF7F2] text-[#111] rounded-3xl p-5 shadow-2xl" data-testid="board-clue-card">
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#8a6d35]">Clue {st.pending.clue + 1} · about {who}</p>
      <p className="font-serif text-2xl mt-1 mb-4">{c.q}</p>
      <div className="grid grid-cols-2 gap-2">
        {c.options.map((o, i) => (
          <button key={i} disabled={disabled || !onPick} onClick={() => onPick?.(i)} data-testid={`board-option-${i}`}
            className={`rounded-xl px-3 py-3 text-sm text-left border ${result?.correct_index === i ? "bg-emerald-500 text-white border-emerald-500" : result?.choice === i ? "bg-rose-500 text-white border-rose-500" : "border-[#C8A96B]/40 bg-white"} ${onPick ? "hover:border-[#C8A96B]" : ""}`}>{o}</button>
        ))}
      </div>
    </motion.div>
  );
}
